import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { Form, Input, message, InputNumber, Button, Modal, Upload, Image as AntdImage } from 'antd';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import styles from './AdminPanel.module.css';
import { Select } from 'antd';
import { updatePackage, deletePackage, fetchTags, packageCheckUp, fetchPackageImages, uploadImages } from './services/api';

const FORM_CONSTANTS = {
  MAX_PACKAGE_NAME_LENGTH: 150,
  MAX_IMAGES: 4,
  MAX_FILE_SIZE: 5 * 1024 * 1024,
  ALLOWED_IMAGE_TYPES: ['image/jpeg', 'image/jpg', 'image/png'],
};

const cleanQuery = (text) => {
  return text?.trim().replace(/\s+/g, ' ').toLowerCase() || '';
};

const EditPackage = () => {
  const { id } = useParams();
  const packageId = parseInt(id, 10);
  const navigate = useNavigate();
  const { state } = useLocation();
  const packageData = state?.packageData;
  const [form] = Form.useForm();
  const [isEditing, setIsEditing] = useState(false);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCheckingPackage, setIsCheckingPackage] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const abortControllerRef = useRef(null);

  const daysCount = parseInt(Form.useWatch('days', form)) || 1;
  const nights = parseInt(Form.useWatch('nights', form)) || 0;
  const nightWiseStays = Form.useWatch('night_wise_stays', form) || [];
  const currentNightsSum = nightWiseStays.reduce((sum, stay) => sum + (stay?.nights || 0), 0);

  const validatePackageName = useCallback(
    async (rule, value) => {
      const cleanedValue = cleanQuery(value);
      if (!cleanedValue) {
        throw new Error('Package name is required');
      }
      if (cleanedValue.length > FORM_CONSTANTS.MAX_PACKAGE_NAME_LENGTH) {
        throw new Error(`Package name must be ${FORM_CONSTANTS.MAX_PACKAGE_NAME_LENGTH} characters or less`);
      }
      if (cleanedValue === packageData?.package_name?.toLowerCase()) {
        return Promise.resolve();
      }

      setIsCheckingPackage(true);
      try {
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
        abortControllerRef.current = new AbortController();
        const { count } = await packageCheckUp(cleanedValue, abortControllerRef.current.signal, packageId);
        if (count > 0) {
          throw new Error('This package name is already used');
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Package name check failed:', error.message);
          throw new Error('Failed to check package name');
        }
        throw error;
      } finally {
        setIsCheckingPackage(false);
      }
    },
    [packageData, packageId]
  );

const getBase64 = useCallback(
    (file) =>
      new Promise((resolve, reject) => {
        if (!file) {
          reject(new Error('No file provided'));
          return;
        }
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = (error) => reject(new Error(`FileReader error: ${error.message}`));
      }),
    []
  );


 const handlePreview = useCallback(
    async (file) => {
      try {
        if (!file.url && !file.preview && file.originFileObj) {
          file.preview = await getBase64(file.originFileObj);
        }
        setPreviewImage(file.url || file.preview);
        setPreviewOpen(true);
      } catch (error) {
        console.error('Preview failed:', error.message);
        message.error('Failed to preview image');
      }
    },
    [getBase64]
  );

  const handleChange = useCallback(
    ({ fileList: newFileList }) => {
      const validFileList = newFileList.filter((file) => file.status !== 'removed');
      const positionsInUse = validFileList.map((file) => file.position).filter((pos) => Number.isInteger(pos) && pos >= 1 && pos <= FORM_CONSTANTS.MAX_IMAGES);

      const updatedFileList = validFileList.map((file, index) => {
        const isOversized = file.originFileObj && file.originFileObj.size > FORM_CONSTANTS.MAX_FILE_SIZE;
        if (!Number.isInteger(file.position)) {
          for (let pos = 1; pos <= FORM_CONSTANTS.MAX_IMAGES; pos++) {
            if (!positionsInUse.includes(pos)) {
              positionsInUse.push(pos);
              return { ...file, position: pos, uid: file.uid || `new-${index}-${Date.now()}` };
            }
          }
          message.error('No available positions for new image');
          return null;
        }
        return { ...file, uid: file.uid || `new-${index}-${Date.now()}`, sizeError: isOversized };
      }).filter(Boolean);

       if (updatedFileList.some((file) => file.sizeError)) {
        message.error('Some images exceed 5MB limit and are highlighted in red. Please remove them.');
      }

      setFileList(updatedFileList);
      form.setFieldsValue({ images: updatedFileList });
    },
    [form]
  );

  const beforeUpload = useCallback(
    (file) => {
      if (!FORM_CONSTANTS.ALLOWED_IMAGE_TYPES.includes(file.type)) {
        message.error('Only JPG, JPEG, or PNG files are allowed');
        return false;
      }
      if (file.size > FORM_CONSTANTS.MAX_FILE_SIZE) {
        message.error('File size must be less than 5MB');
        return false;
      }
      return true;
    },
    []
  );

    const retryUpload = async (files, packageName, signal, maxRetries = 3) => {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await uploadImages(files, packageName, signal);
      } catch (error) {
        if (error.name === 'AbortError') {
          throw error;
        }
        console.warn(`Upload attempt ${attempt} failed: ${error.message}`);
        if (attempt < maxRetries) {
          await new Promise((resolve) => setTimeout(resolve, 1000));
          continue;
        }
        throw new Error(`Failed to upload images after ${maxRetries} attempts: ${error.message}`);
      }
    }
  };


   useEffect(() => {
    if (isNaN(packageId)) {
      message.error('Invalid package ID');
      navigate(-1);
      return;
    }

    if (!packageData) {
      message.error('No package data available');
      navigate(-1);
      return;
    }

    const controller = new AbortController();
    Promise.all([
      fetchPackageImages(packageData.package_name, controller.signal),
      Promise.resolve(packageData),
    ])
      .then(([images]) => {
        const formattedData = {
          ...packageData,
          days: parseInt(packageData.days, 10) || null,
          nights: parseInt(packageData.nights, 10) || null,
          day_details: packageData.day_details?.map((day) => ({
            day_number: parseInt(day.day_number, 10) || null,
            heading: day.heading || '',
            activity: day.activity || '',
          })) || [],
          place_details: packageData.place_details?.map((place) => ({
            place: place.place || '',
            nights: parseInt(place.nights, 10) || null,
          })) || [],
        };

        const formattedImages = images.map((img, index) => ({
          uid: `existing-${index + 1}`,
          name: `image-${index + 1}.jpg`,
          status: 'done',
          url: img,
          position: index + 1,
        }));

        form.setFieldsValue({
          package_name: formattedData.package_name,
          place_name: formattedData.place_name,
          days: formattedData.days,
          nights: formattedData.nights,
          description: formattedData.description,
          overview: formattedData.overview,
          highlights: formattedData.highlights || [],
          inclusions: formattedData.inclusions || [],
          exclusions: formattedData.exclusions || [],
          note: formattedData.note || [],
          day_wise_activities: formattedData.day_details?.map((day) => ({
            day: day.day_number,
            heading: day.heading,
            activity: day.activity,
          })) || [],
          night_wise_stays: formattedData.place_details?.map((place) => ({
            place: place.place,
            nights: place.nights,
          })) || [],
          tags: formattedData.tags || [],
          images: formattedImages,
        });

        setFileList(formattedImages);
        setLoading(false);
      })
      .catch((error) => {
        if (error.name !== 'AbortError') {
          console.error('Failed to load package data:', error.message);
          message.error('Failed to load package data');
          navigate(-1);
        }
      });

    return () => controller.abort();
  }, [packageData, form, navigate, packageId]);

  useEffect(() => {
    const controller = new AbortController();
    fetchTags(controller.signal)
      .then((data) => setTags(data))
      .catch((error) => {
        if (error.name !== 'AbortError') {
          console.error('Failed to load tags:', error.message);
          message.error('Failed to load tags');
        }
      });
    return () => controller.abort();
  }, []);

  const resetFormToOriginal = useCallback(() => {
    if (!packageData) return;

    const controller = new AbortController();
    fetchPackageImages(packageData.package_name, controller.signal)
      .then((images) => {
        const formattedImages = images.map((img, index) => ({
          uid: `existing-${index + 1}`,
          name: `image-${index + 1}.jpg`,
          status: 'done',
          url: img,
          position: index + 1,
        }));

        form.setFieldsValue({
          package_name: packageData.package_name,
          place_name: packageData.place_name,
          days: parseInt(packageData.days, 10) || null,
          nights: parseInt(packageData.nights, 10) || null,
          description: packageData.description,
          overview: packageData.overview,
          highlights: packageData.highlights || [],
          inclusions: packageData.inclusions || [],
          exclusions: packageData.exclusions || [],
          note: packageData.note || [],
          day_wise_activities: packageData.day_details?.map((day) => ({
            day: parseInt(day.day_number, 10),
            heading: day.heading,
            activity: day.activity,
          })) || [],
          night_wise_stays: packageData.place_details?.map((place) => ({
            place: place.place,
            nights: parseInt(place.nights, 10),
          })) || [],
          tags: packageData.tags || [],
          images: formattedImages,
        });

        setFileList(formattedImages);
      })
      .catch((error) => {
        if (error.name !== 'AbortError') {
          console.error('Failed to reset form:', error.message);
          message.error('Failed to reset images');
        }
      });

    return () => controller.abort();
  }, [form, packageData]);

  

  const onFinish = useCallback(
    async (values) => {
      let controller = new AbortController();
      try {
        setIsUploading(true);
        abortControllerRef.current = controller;

        const updatedValues = {
          packageId,
          package_name: values.package_name?.trim()?.toLowerCase(),
          place_name: values.place_name?.trim()?.toLowerCase(),
          days: parseInt(values.days, 10),
          nights: parseInt(values.nights, 10),
          description: values.description?.trim()?.toLowerCase(),
          overview: values.overview?.trim()?.toLowerCase(),
          highlights: values.highlights?.length > 0
            ? values.highlights.filter((h) => h && typeof h === 'string' && h.trim() !== '').map((h) => h.trim().toLowerCase())
            : null,
          inclusions: values.inclusions?.length > 0
            ? values.inclusions.filter((i) => i && typeof i === 'string' && i.trim() !== '').map((i) => i.trim().toLowerCase())
            : null,
          exclusions: values.exclusions?.length > 0
            ? values.exclusions.filter((e) => e && typeof e === 'string' && e.trim() !== '').map((e) => e.trim().toLowerCase())
            : null,
          note: values.note?.length > 0
            ? values.note.filter((n) => n && typeof n === 'string' && n.trim() !== '').map((n) => n.trim().toLowerCase())
            : null,
          day_wise_activities: values.day_wise_activities?.length > 0
            ? values.day_wise_activities
                .filter(
                  (activity) =>
                    activity &&
                    typeof activity === 'object' &&
                    activity.day &&
                    typeof activity.day === 'number' &&
                    activity.day >= 1 &&
                    activity.heading &&
                    typeof activity.heading === 'string' &&
                    activity.heading.trim() !== '' &&
                    activity.activity &&
                    typeof activity.activity === 'string' &&
                    activity.activity.trim() !== ''
                )
                .map((activity) => ({
                  day: parseInt(activity.day, 10),
                  heading: activity.heading.trim().toLowerCase(),
                  activity: activity.activity.trim().toLowerCase(),
                }))
            : null,
          night_wise_stays: values.night_wise_stays?.length > 0
            ? values.night_wise_stays
                .filter(
                  (stay) =>
                    stay &&
                    typeof stay === 'object' &&
                    stay.nights &&
                    typeof stay.nights === 'number' &&
                    stay.nights >= 0 &&
                    stay.place &&
                    typeof stay.place === 'string' &&
                    stay.place.trim() !== ''
                )
                .map((stay) => ({
                  nights: parseInt(stay.nights, 10),
                  place: stay.place.trim().toLowerCase(),
                }))
            : null,
          tags: values.tags?.length > 0
            ? values.tags.filter((t) => t && typeof t === 'string' && t.trim() !== '').map((t) => t.trim().toLowerCase())
            : null,
        };

        const cleanValues = Object.fromEntries(
          Object.entries(updatedValues).filter(([_, value]) => value !== undefined)
        );
        const response = await updatePackage(cleanValues, controller.signal);
        if (response.status !== 201) throw new Error('Failed to update package data');

        const changedFiles = fileList
          .filter((file) => file.originFileObj)
          .map((file) => ({
            file: file.originFileObj,
            position: file.position,
          }));

        if (changedFiles.length > 0) {
        
        for (const { file } of changedFiles) {
          if (file.size > FORM_CONSTANTS.MAX_FILE_SIZE) {
            throw new Error(`File ${file.name} exceeds 5MB limit`);
          }
        }
        await retryUpload(changedFiles, packageId, controller.signal);
      }

        message.success('Package updated successfully');
        setIsEditing(false);
        const images = await fetchPackageImages(cleanValues.package_name, controller.signal);
        const formattedImages = images.map((img, index) => ({
          uid: `existing-${index + 1}`,
          name: `image-${index + 1}.jpg`,
          status: 'done',
          url: img,
          position: index + 1,
        }));
        setFileList(formattedImages);
        form.setFieldsValue({ images: formattedImages });
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Package update failed:', error.message);
          message.error(`Failed to update package, retry uploading images`);
        }
      } finally {
        setIsUploading(false);
        abortControllerRef.current = null;
        controller = null;
      }
    },
    [packageId, fileList,form]
  );

 const handleDelete = useCallback(() => {
    Modal.confirm({
      title: 'Are you sure you want to delete this package?',
      content: 'This action cannot be undone.',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk: async () => {
        try {
          const controller = new AbortController();
          const result = await deletePackage(packageId, controller.signal);
    
          if (result.status === 200) {
            message.success('Package deleted successfully');
            navigate(-1);
          } else {
            throw new Error(`Delete failed with status ${result.status}`);
          }
        } catch (error) {
          if (error.name !== 'AbortError') {
            console.error('Package deletion failed:', error.message);
            message.error(`Failed to delete package: ${error.message}`);
          }
        }
      },
    });
  }, [navigate, packageId]);

  if (loading) return <div>Loading...</div>;

  return (
    <div className={styles.addPackageWrapper}>
      <div className={styles.addPackageContainer}>
        <h2 className={styles.formTitle}>Edit & Delete Package</h2>
        <div style={{ fontWeight: 'normal', fontSize: '12px', textTransform: 'lowercase', width:'100%', justifyContent:'center', display:'flex',color:'red', position:'relative'}}>
          <ul>
            <li style={{marginBottom:'0.5rem'}}>use lower-case letters</li>
            <li style={{marginBottom:'0.5rem'}}>fill all the fields having * symbol</li>
            <li style={{marginBottom:'0.5rem'}}>Only images with jpg, jpeg, png formats are allowed</li>
            <li style={{marginBottom:'0.5rem'}}>Image size should be less than 5MB</li>
          </ul>
        </div>
        <Form
          form={form}
          labelCol={{ span: 4 }}
          wrapperCol={{ span: 20 }}
          layout="horizontal"
          onFinish={onFinish}
          onFinishFailed={() => message.error('Please fill all required fields correctly')}
          requiredMark={false}
          scrollToFirstError
        >
          <Form.Item
            label={<span><span className={styles.required}>*</span> Package Name</span>}
            name="package_name"
            hasFeedback
            validateTrigger={['onChange']}
            validateDebounce={1000}
            rules={[{ validator: validatePackageName }]}
            validateStatus={isCheckingPackage ? 'validating' : undefined}
          >
            <Input disabled={!isEditing} placeholder="Enter package name" id="package_name" aria-label="Package name" />
          </Form.Item>
          <Form.Item
            label={<span><span className={styles.required}>*</span> Place Name</span>}
            name="place_name"
            rules={[{ required: true, message: 'Place name is required' }]}
          >
            <Input disabled={!isEditing} placeholder="Enter place name" id="place_name" aria-label="Place name" />
          </Form.Item>
          <Form.Item
            label={<span><span className={styles.required}>*</span> Days</span>}
            name="days"
            rules={[
              { required: true, message: 'Days is required' },
              { type: 'number', min: 1, message: 'Days must be at least 1' },
            ]}
          >
            <InputNumber
              min={1}
              disabled={!isEditing}
              placeholder="Days"
              id="days"
              aria-label="Days"
              onChange={() => {
                const currentActivities = form.getFieldValue('day_wise_activities') || [];
                if (currentActivities.length > daysCount) {
                  form.setFieldsValue({
                    day_wise_activities: currentActivities.slice(0, daysCount),
                  });
                }
              }}
            />
          </Form.Item>
          <Form.Item
            label={<span><span className={styles.required}>*</span> Nights</span>}
            name="nights"
            rules={[
              { required: true, message: 'Nights is required' },
              { type: 'number', min: 1, message: 'Nights must be at least 1' },
              {
                validator: (_, value) => {
                  if (value && currentNightsSum > value) {
                    return Promise.reject(new Error(`Total nights in stays (${currentNightsSum}) exceeds package nights (${value})`));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <InputNumber
              min={1}
              disabled={!isEditing}
              placeholder="Nights"
              id="nights"
              aria-label="Nights"
              onChange={() => {
                const stays = form.getFieldValue('night_wise_stays') || [];
                let sum = 0;
                const validStays = stays.filter((stay) => {
                  if (!stay || !stay.nights) return false;
                  if (sum + stay.nights <= nights) {
                    sum += stay.nights;
                    return true;
                  }
                  return false;
                });
                if (validStays.length < stays.length) {
                  form.setFieldsValue({ night_wise_stays: validStays });
                }
              }}
            />
          </Form.Item>
          <Form.Item label="Overview" name="overview">
            <Input.TextArea rows={6} disabled={!isEditing} placeholder="Provide a brief overview of the package" id="overview" aria-label="Overview" />
          </Form.Item>
          <Form.List name="highlights" >
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }, index) => (
                  <Form.Item
                    key={key}
                    label={index === 0 ? 'Highlights' : ''}
                    wrapperCol={{ offset: index === 0 ? 0 : 4 }}
                  >
                    <div className={styles.dynamicField}>
                      <Form.Item
                        {...restField}
                        name={name}
                        rules={[{ required: true, message: 'Highlight cannot be empty' }]}
                        noStyle
                      >
                        <Input
                          style={{ width: 'calc(100% - 32px)' }}
                          disabled={!isEditing}
                          placeholder="Enter a highlight"
                          id={`highlights_${name}`}
                          aria-label={`Highlight ${index + 1}`}
                        />
                      </Form.Item>
                      {isEditing && (
                        <MinusCircleOutlined
                          onClick={() => remove(name)}
                          className={styles.deleteIcon}
                          aria-label={`Remove highlight ${index + 1}`}
                        />
                      )}
                    </div>
                  </Form.Item>
                ))}
                <Form.Item wrapperCol={{ offset: 4 }}>
                  <Button
                    type="dashed"
                    onClick={() => add()}
                    block
                    icon={<PlusOutlined />}
                    disabled={!isEditing}
                    className={styles.addButton}
                    aria-label="Add highlight"
                  >
                    Add Highlight
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>
          <Form.Item label="Description" name="description">
            <Input.TextArea
              rows={5}
              disabled={!isEditing}
              placeholder="Provide a description for search (e.g., beach holiday adventure)"
              id="description"
              aria-label="Description"
            />
          </Form.Item>
          <Form.List name="inclusions">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }, index) => (
                  <Form.Item
                    key={key}
                    label={index === 0 ? 'Inclusions' : ''}
                    wrapperCol={{ offset: index === 0 ? 0 : 4 }}
                  >
                    <div className={styles.dynamicField}>
                      <Form.Item
                        {...restField}
                        name={name}
                        rules={[{ required: true, message: 'Inclusion cannot be empty' }]}
                        noStyle
                      >
                        <Input
                          style={{ width: 'calc(100% - 32px)' }}
                          disabled={!isEditing}
                          placeholder="Enter an inclusion"
                          id={`inclusions_${name}`}
                          aria-label={`Inclusion ${index + 1}`}
                        />
                      </Form.Item>
                      {isEditing && (
                        <MinusCircleOutlined
                          onClick={() => remove(name)}
                          className={styles.deleteIcon}
                          aria-label={`Remove inclusion ${index + 1}`}
                        />
                      )}
                    </div>
                  </Form.Item>
                ))}
                <Form.Item wrapperCol={{ offset: 4 }}>
                  <Button
                    type="dashed"
                    onClick={() => add()}
                    block
                    icon={<PlusOutlined />}
                    disabled={!isEditing}
                    className={styles.addButton}
                    aria-label="Add inclusion"
                  >
                    Add Inclusion
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>
          <Form.List name="exclusions">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }, index) => (
                  <Form.Item
                    key={key}
                    label={index === 0 ? 'Exclusions' : ''}
                    wrapperCol={{ offset: index === 0 ? 0 : 4 }}
                  >
                    <div className={styles.dynamicField}>
                      <Form.Item
                        {...restField}
                        name={name}
                        rules={[{ required: true, message: 'Exclusion cannot be empty' }]}
                        noStyle
                      >
                        <Input
                          style={{ width: 'calc(100% - 32px)' }}
                          disabled={!isEditing}
                          placeholder="Enter an exclusion"
                          id={`exclusions_${name}`}
                          aria-label={`Exclusion ${index + 1}`}
                        />
                      </Form.Item>
                      {isEditing && (
                        <MinusCircleOutlined
                          onClick={() => remove(name)}
                          className={styles.deleteIcon}
                          aria-label={`Remove exclusion ${index + 1}`}
                        />
                      )}
                    </div>
                  </Form.Item>
                ))}
                <Form.Item wrapperCol={{ offset: 4 }}>
                  <Button
                    type="dashed"
                    onClick={() => add()}
                    block
                    icon={<PlusOutlined />}
                    disabled={!isEditing}
                    className={styles.addButton}
                    aria-label="Add exclusion"
                  >
                    Add Exclusion
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>
          <Form.Item label="Tags" name="tags">
            <Select
              mode="multiple"
              allowClear
              style={{ width: '100%' }}
              placeholder="Select tags for the package"
              options={tags.reduce((acc, { tag, category }) => {
                let group = acc.find((g) => g.label === category);
                if (!group) {
                  group = { label: category, options: [] };
                  acc.push(group);
                }
                group.options.push({ label: tag, value: tag });
                return acc;
              }, [])}
              disabled={!isEditing}
              id="tags"
              aria-label="Tags"
            />
          </Form.Item>
          <Form.List name="note">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }, index) => (
                  <Form.Item
                    key={key}
                    label={index === 0 ? 'Notes' : ''}
                    wrapperCol={{ offset: index === 0 ? 0 : 4 }}
                  >
                    <div className={styles.dynamicField}>
                      <Form.Item
                        {...restField}
                        name={name}
                        rules={[{ required: true, message: 'Note cannot be empty' }]}
                        noStyle
                      >
                        <Input
                          style={{ width: 'calc(100% - 32px)' }}
                          disabled={!isEditing}
                          placeholder="Enter a note"
                          id={`note_${name}`}
                          aria-label={`Note ${index + 1}`}
                        />
                      </Form.Item>
                      {isEditing && (
                        <MinusCircleOutlined
                          onClick={() => remove(name)}
                          className={styles.deleteIcon}
                          aria-label={`Remove note ${index + 1}`}
                        />
                      )}
                    </div>
                  </Form.Item>
                ))}
                <Form.Item wrapperCol={{ offset: 4 }}>
                  <Button
                    type="dashed"
                    onClick={() => add()}
                    block
                    icon={<PlusOutlined />}
                    disabled={!isEditing}
                    className={styles.addButton}
                    aria-label="Add note"
                  >
                    Add Note
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>
          <Form.Item label={<span><span className={styles.required}>*</span> Days-Wise Activities</span>} className={styles.activitySection}>
            <Form.List
              name="day_wise_activities"
              rules={[{ required: true, message: 'At least one activity is required' }]}
            >
              {(fields, { add, remove }) => (
                <div className={styles.activityList}>
                  {fields.map(({ key, name, ...restField }, index) => (
                    <div key={key} className={styles.activityItem}>
                      <div className={styles.activityFields}>
                        <Form.Item
                          {...restField}
                          name={[name, 'day']}
                          rules={[{ required: true, message: 'Day is required' }]}
                          initialValue={index + 1}
                        >
                          <InputNumber
                            min={1}
                            disabled={!isEditing}
                            placeholder="Day"
                            id={`day_wise_activities_${name}_day`}
                            aria-label={`Day ${index + 1}`}
                          />
                        </Form.Item>
                        <Form.Item
                          {...restField}
                          name={[name, 'heading']}
                          style={{ width: '50%' }}
                          rules={[{ required: true, message: 'Heading is required' }]}
                        >
                          <Input
                            disabled={!isEditing}
                            placeholder="Enter activity heading"
                            id={`day_wise_activities_${name}_heading`}
                            aria-label={`Heading for Day ${index + 1}`}
                          />
                        </Form.Item>
                        <Form.Item
                          {...restField}
                          name={[name, 'activity']}
                          style={{ width: '100%' }}
                          rules={[{ required: true, message: 'Activity is required' }]}
                        >
                          <Input.TextArea
                            rows={6}
                            disabled={!isEditing}
                            placeholder="Enter activity details"
                            id={`day_wise_activities_${name}_activity`}
                            aria-label={`Activity for Day ${index + 1}`}
                          />
                        </Form.Item>
                        {isEditing && (
                          <button
                            onClick={() => remove(name)}
                            className={styles.deleteButton}
                            aria-label={`Remove activity ${index + 1}`}
                          >
                            <MinusCircleOutlined />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                  <Button
                    type="dashed"
                    onClick={() => add({ day: fields.length + 1 })}
                    block
                    icon={<PlusOutlined />}
                    disabled={!isEditing || fields.length >= daysCount}
                    className={styles.addButton}
                    aria-label="Add activity"
                  >
                    Add Day-wise Activity
                  </Button>
                </div>
              )}
            </Form.List>
          </Form.Item>
          <Form.Item label="Night-wise Stays" className={styles.activitySection}>
            <Form.List name="night_wise_stays">
              {(fields, { add, remove }) => (
                <div className={styles.activityList}>
                  {fields.map(({ key, name, ...restField }, index) => (
                    <div key={key} className={styles.activityItem}>
                      <div className={styles.activityFields}>
                        <Form.Item
                          {...restField}
                          name={[name, 'nights']}
                          rules={[
                            { required: true, message: 'Nights is required' },
                            { type: 'number', min: 1, message: 'Nights must be at least 1' },
                            {
                              validator: (_, value) => {
                                const stays = form.getFieldValue('night_wise_stays') || [];
                                const sum = stays.reduce(
                                  (acc, stay, idx) => acc + (idx === name ? (value || 0) : (stay?.nights || 0)),
                                  0
                                );
                                if (sum > nights) {
                                  return Promise.reject(new Error(`Total nights (${sum}) exceeds package nights (${nights})`));
                                }
                                return Promise.resolve();
                              },
                            },
                          ]}
                        >
                          <InputNumber
                            min={1}
                            disabled={!isEditing}
                            placeholder="Nights"
                            id={`night_wise_stays_${name}_nights`}
                            aria-label={`Nights for stay ${index + 1}`}
                          />
                        </Form.Item>
                        <Form.Item
                          {...restField}
                          name={[name, 'place']}
                          style={{ width: '50%' }}
                          rules={[{ required: true, message: 'Place is required' }]}
                        >
                          <Input
                            disabled={!isEditing}
                            placeholder="Enter place name"
                            id={`night_wise_stays_${name}_place`}
                            aria-label={`Place for stay ${index + 1}`}
                          />
                        </Form.Item>
                        {isEditing && (
                          <button
                            onClick={() => remove(name)}
                            className={styles.deleteButton}
                            aria-label={`Remove stay ${index + 1}`}
                          >
                            <MinusCircleOutlined />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                  <Button
                    type="dashed"
                    onClick={() => add()}
                    block
                    icon={<PlusOutlined />}
                    disabled={!isEditing || currentNightsSum >= nights}
                    className={styles.addButton}
                    aria-label="Add stay"
                  >
                    Add Night-wise Stay
                  </Button>
                </div>
              )}
            </Form.List>
          </Form.Item>
          <Form.Item
            label={<span><span className={styles.required}>*</span> Images</span>}
            name="images"
            rules={[
              {
                validator: (_, value) => {
                  if (!value || value.length !== FORM_CONSTANTS.MAX_IMAGES) {
                    return Promise.reject(new Error(`Exactly ${FORM_CONSTANTS.MAX_IMAGES} images are required`));
                  }
                  if (value.some((file) => file.status === 'error' || !file.position)) {
                    return Promise.reject(new Error('All images must be valid with assigned positions'));
                  }
                  if (value.some((file) => file.sizeError)) {
                    return Promise.reject(new Error('Some images exceed 5MB limit. Please remove highlighted images.'));
                  }
                  return Promise.resolve();
                },
              },
            ]}
            getValueFromEvent={({ fileList }) => fileList}
          >
            <Upload
              action="/admin/upload"
              listType="picture-card"
              fileList={fileList}
              onPreview={handlePreview}
              onChange={handleChange}
              beforeUpload={beforeUpload}
              className={styles.uploadButton}
              customRequest={({ onSuccess, onError, file }) => {
                setTimeout(() => {
                  if (beforeUpload(file)) {
                    onSuccess('ok');
                  } else {
                    onError(new Error('Upload validation failed'));
                  }
                }, 0);
              }}
              accept={FORM_CONSTANTS.ALLOWED_IMAGE_TYPES.join(',')}
              disabled={!isEditing}
              maxCount={FORM_CONSTANTS.MAX_IMAGES}
              id="images"
              aria-label="Images upload"
              itemRender={(originNode, file) => (
                <div style={{ border: file.sizeError ? '2px solid red' : 'none', padding: file.sizeError ? '2px' : '0' }}>
                  {originNode}
                </div>
              )}
            >
              {fileList.length >= FORM_CONSTANTS.MAX_IMAGES ? null : (
                <button style={{ border: 0, background: 'none' }} type="button" aria-label="Upload image">
                  <PlusOutlined />
                  <div style={{ marginTop: 8 }}>Upload</div>
                </button>
              )}
            </Upload>
          </Form.Item>
          {previewImage && (
            <AntdImage
              wrapperStyle={{ display: 'none' }}
              preview={{
                visible: previewOpen,
                onVisibleChange: (visible) => setPreviewOpen(visible),
                afterOpenChange: (visible) => !visible && setPreviewImage(''),
              }}
              src={previewImage}
              alt="Image preview"
            />
          )}
          <Form.Item wrapperCol={{ offset: 4 }}>
            {isEditing || isUploading ? (
              <>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={isUploading}
                  disabled={isCheckingPackage}
                  aria-label="Save changes"
                >
                  Save Changes
                </Button>

                {!isUploading && (
                  <Button
                  type="button"
                  disabled={isUploading || isCheckingPackage}
                  onClick={() => {
                    resetFormToOriginal();
                    setIsEditing(false);
                    setFileList(form.getFieldValue('images') || []);
                  }}
                  style={{ marginLeft: 8 }}
                  aria-label="Cancel"
                >
                  Cancel
                </Button>
                )}
              </>
            ) : (
              <>
                <Button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    setIsEditing(true);
                  }}
                  disabled={loading}
                  aria-label="Edit package"
                >
                  Edit
                </Button>
                <Button
                  type="button"
                  onClick={handleDelete}
                  danger
                  style={{ marginLeft: 8 }}
                  aria-label="Delete package"
                >
                  Delete
                </Button>
              </>
            )}
          </Form.Item>
        </Form>
      </div>
    </div>
  );
};

export default EditPackage;